<template>
  <div v-if="resource" class="flex flex-col gap-3">
    <div class="bg-white rounded-lg border">
      <Accordion
        :id="`tech-timeline-${technician.technician_id}`"
        accordion-type="flush"
        :items="[{ open: expanded }]"
        @expanded="toggleShowBadge"
      >
        <template #content-0-header>
          <div
            class="text-sm flex flex-col gap-4 md:gap-0 md:flex-row-reverse md:justify-between md:items-center w-full"
          >
            <div class="flex items-center gap-2">
              <TimeBadge
                v-if="showBadge"
                :label="timeBadge.label"
                :time="timeBadge.time"
                :text-only="timeBadge.textOnly || timeBadge.time === '--'"
                :background-color="timeBadge.backgroundColor"
              >
                <template #icon
                  ><component :is="timeBadge.icon"></component
                ></template>
              </TimeBadge>
              <button
                v-if="canEdit"
                data-test="edit-tech-times"
                title="Edit technician times"
                @click.stop="editModalOpen = true"
              >
                <i class="far fa-edit link ml-1" />
              </button>
            </div>
            <ResourceContent :resource="resource" :technician-data="technician">
              <a
                v-if="technician.phone"
                data-cy="tech-phone"
                :href="`tel:${technician.phone}`"
                class="text-xs text-gray-500 hover:text-blue-600"
              >
                <i class="fa-regular fa-phone mr-1"></i>{{ technician.phone }}
              </a>
            </ResourceContent>
          </div>
        </template>
        <template #content-0-body>
          <AppointmentTimeline :appointment="effectiveAppointment" />
        </template>
      </Accordion>
    </div>
    <TechnicianTimeEditModal
      v-if="editModalOpen"
      :appointment="appointment"
      :appointment-technician-entry="appointmentTechnicianEntry"
      :technician-profile-id="appointmentTechnicianEntry?.technician_profile_id"
      @close="editModalOpen = false"
      @refresh-appointment="emit('refresh-appointment')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";

import Accordion from "@/components/Seranova/Accordion/Accordion.vue";
import TimeBadge from "@/components/Seranova/TimeBadge/TimeBadge.vue";
import { useAppointmentTimes } from "@/AppointmentDetails/useAppointmentTimes";
import ResourceContent from "@/SchedulerV2/ResourceContent.vue";
import {
  AppointmentTechnicianEntry,
  AppointmentV2,
  mapTechStatusToAppointmentStatus
} from "@/typings/appointment";
import { TimelineResource } from "@/typings/scheduler";
import { Technician } from "@/typings/technician";
import AppointmentTimeline from "./AppointmentTimeline.vue";
import TechnicianTimeEditModal from "./TechnicianTimeEditModal.vue";

const emit = defineEmits<{
  "refresh-appointment": [];
}>();

const props = defineProps<{
  technician: Technician;
  appointment: AppointmentV2;
  appointmentTechnicianEntry?: AppointmentTechnicianEntry;
  expanded?: boolean;
}>();

const editModalOpen = ref(false);

const canEdit = computed(() => props.appointment.status !== "cancelled");

const effectiveAppointment = computed(() => {
  if (!props.appointmentTechnicianEntry) return props.appointment;
  const at = props.appointmentTechnicianEntry;
  return {
    ...props.appointment,
    accepted_time: at.accepted_time ?? null,
    dispatch_time: at.dispatch_time ?? "",
    arrival_time: at.arrival_time ?? "",
    departure_time: at.departure_time ?? "",
    pre_work_minutes: at.pre_work_minutes,
    travel_minutes: at.travel_minutes,
    work_minutes: at.work_minutes,
    duration_minutes: at.duration_minutes,
    status: mapTechStatusToAppointmentStatus(at.technician_status)
  };
});

const effectiveProps = reactive({ appointment: effectiveAppointment });
const { timeBadge } = useAppointmentTimes(effectiveProps);
const showBadge = ref(!props.expanded);

const resource = computed(() => {
  if (!props.technician) return null;
  return new TimelineResource({
    id: props.technician.technician_id,
    name: props.technician?.full_name
  });
});

const toggleShowBadge = () => {
  showBadge.value = !showBadge.value;
};
</script>

<style scoped></style>
